// components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm.tsx
import { Input, Select, DatePicker, Radio, Switch } from "antd";
import dayjs from "dayjs";
import RangeInput from "../../../ExploreArchive/RangeInput";
import NumberInput from "../../../ExploreArchive/NumberInput";
import SlideInput from "../../../ExploreArchive/SlideInput";
import { useTranslation } from "react-i18next";
import CommonIcons from "../../../../../assets/icons";
import { isFieldEditable } from "../../ultis";
import {
   buildDisabledDate,
   buildDisabledTime,
   getDateDisplayFormat,
   getDateFieldBounds,
   getDateFieldRuleHints,
   isPartnerValueStale,
   isRangeFromSide,
   resolveRangePartnerKey,
} from "../../dateFieldRules";

/**
 * Parse a range field key into its role (min/max) and base name, so min/max
 * halves can be paired by name regardless of position.
 *   min_off_nadir             -> { isMin: true,  base: "off_nadir" }
 *   off_nadir_max             -> { isMax: true,  base: "off_nadir" }
 *   synspective:min_off_nadir -> { isMin: true,  base: "synspective_off_nadir" }
 *
 * Provider-namespaced keys arrive as `provider:min_xxx`. The `:` (and the `.`
 * or `-` other schemas use) is normalised to `_` first, otherwise the min/max
 * token sits next to a separator the word-boundary regex below does not
 * accept and the field looks like an unpaired range. The namespace stays part
 * of `base`, so two providers' off-nadir fields never pair with each other.
 */
/**
 * Same Dayjs instance for the same stored value. A fresh `dayjs(value)` on every
 * render makes the picker treat each parent re-render as a new selection and
 * snap its panel back to the picked month, so the header arrows look dead once
 * a date is chosen.
 */
const stableDayjsCache = new Map<string, dayjs.Dayjs>();
const toStableDayjs = (value: any): dayjs.Dayjs | null =>
{
   if (!value) return null;
   const cacheKey = String(value);
   let cached = stableDayjsCache.get(cacheKey);
   if (!cached)
   {
      if (stableDayjsCache.size > 200) stableDayjsCache.clear();
      cached = dayjs(value);
      stableDayjsCache.set(cacheKey, cached);
   }
   return cached;
};

const getRangeMeta = (key: string) =>
{
   const lower = key.toLowerCase().replace(/[:.\-]/g, "_");
   const isMin = /(^|_)(min|lower)(_|$)/.test(lower);
   const isMax = /(^|_)(max|upper)(_|$)/.test(lower);
   const base = lower
      .replace(/(^|_)(min|max|lower|upper)(_|$)/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
   return { isMin, isMax, base };
};

/**
 * Find the partner key of a `range` field by matching base name + opposite
 * role (min↔max) anywhere in the given order. Returns null if none.
 */
const findRangePartnerKey = (
   key: string,
   order: string[],
   options: Record<string, any>,
): string | null =>
{
   if (options?.[key]?.widget !== "range") return null;
   const me = getRangeMeta(key);
   if (!me.isMin && !me.isMax) return null;

   for (const other of order)
   {
      if (other === key) continue;
      if (options?.[other]?.widget !== "range") continue;
      const om = getRangeMeta(other);
      if (om.base !== me.base) continue;
      if ((me.isMin && om.isMax) || (me.isMax && om.isMin)) return other;
   }
   return null;
};

interface UnifiedDynamicFormProps
{
   step: any;           // contains schema, ui (type field ignored)
   formData: Record<string, any>;
   setFormData: (data: any) => void;
   type?: string; // edit || add
   display?: string; // grid || col
   disablePastDates?: boolean; // default true for tasking (future only). Set false for archive (past only).
}

/**
 * Unified dynamic form renderer that handles all widget types without relying on step.type.
 * Renders fields based on ui.order and ui.options[key].widget.
 *
 * Special handling:
 * - Two consecutive "range" widgets with matching min/max pattern are combined into one RangeInput
 * - The second field is skipped (rendered as part of the pair)
 * - Widgets "rangeInput" and "range_minmax" render a RangeInput that stores value as [min, max] array
 */
const UnifiedDynamicForm = ({
   step,
   formData,
   setFormData,
   type,
   display,
   disablePastDates = true
}: UnifiedDynamicFormProps) =>
{
   const { ui } = step;
   const { t } = useTranslation();

   const isEditable = (config: any) =>
   {
      if (type !== "edit") return true; // add mode => edit all
      return isFieldEditable(config);
   };


   // Check if a field should be rendered based on dependency conditions
   // Supports both new format (depends_on: {field, value}) and legacy format (depField: "fieldName")
   // const shouldRenderField = (key: string, config: any) =>
   // {
   //       if (!config.depends_on && !config.depField) return true;

   //       let depField: string;
   //       let depValue: string;

   //       if (config.depends_on)
   //       {
   //             depField = config.depends_on.field;
   //             depValue = config.depends_on.value;
   //       }
   //       else if (config.depField)
   //       {
   //             depField = config.depField;
   //             depValue = key; // Legacy: field is shown when controller equals this field's key
   //       }

   //       // Get the controller field's value from formData, or its default if not set
   //       const controllerConfig = ui?.options?.[depField];
   //       const controllerDefault = controllerConfig?.default;
   //       const controllerValue = formData[depField] ?? controllerDefault;

   //       return controllerValue === depValue;
   // };

   const shouldRenderField = (key: string, config: any) =>
   {
      if (!config.depends_on && !config.depField) return true;

      const depField = config.depends_on?.field ?? config.depField;
      const depValue = config.depends_on?.value ?? key;

      if (!depField) return true;

      const controllerValue =
         formData[depField] ?? ui?.options?.[depField]?.default;

      return controllerValue === depValue;
   };

   const renderField = (key: string, config: any) =>
   {
      const value = formData[key];

      switch (config.widget)
      {
         case "input":
            return (
               <Input
                  className="w-full div-dropdown"
                  key={key}
                  placeholder={t(config.placeholder)}
                  value={value ?? ""}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
                  minLength={config?.minLength}
                  maxLength={config?.maxLength ?? 100}
               />
            );

         case "textarea":
            return (
               <Input.TextArea
                  className="w-full div-dropdown"
                  key={key}
                  placeholder={t(config.placeholder)}
                  value={value ?? ""}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
                  minLength={config?.minLength}
                  maxLength={config?.maxLength ?? 256}
               />
            );

         case "select":


            return (
               <Select
                  className="w-full div-dropdown"
                  key={key}


                  options={config.options?.map((o: any) => ({
                     label: o.label || o.label || o.value,
                     value: o.value,
                  }))?.concat(config.mode === "multiple"
                     ? [{ label: "All", value: "all" }] : [])}
                  placeholder={t(config.placeholder)}
                  value={value}
                  // onChange={(val) =>
                  //    setFormData((prev: any) => ({
                  //       ...prev,
                  //       [key]: val,
                  //    }))
                  // }
                  onChange={(val) =>
                  {
                     if (config.mode === "multiple")
                     {
                        if (val.includes("all"))
                        {
                           const allValues = config.options?.map((o: any) => o.value);

                           setFormData((prev: any) => ({
                              ...prev,
                              [key]: allValues,
                           }));

                           return;
                        }
                     }

                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }));
                  }}
                  disabled={!isEditable(config)}
                  mode={config.mode}
               />
            );

         case "select-acquisition": {
            // Stores { value, geometry } of the selected option into formData[key]
            const acqValue = typeof value === "object" ? value?.value : value;
            return (
               <Select
                  className="w-full div-dropdown"
                  key={key}
                  options={config.options?.map((o: any) => ({
                     label: o.label || o.value,
                     value: o.value,
                  }))}
                  placeholder={t(config.placeholder)}
                  value={acqValue}
                  onChange={(val) =>
                  {
                     const selected = config.options?.find((o: any) => o.value === val);
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: selected ? { value: selected.value, geometry: selected.geometry } : val,
                     }));
                  }}
                  disabled={!isEditable(config)}
               />
            );
         }

         case "date":
         case "date-picker": {
            const dateValue = toStableDayjs(value);
            const options = ui?.options || {};

            /**
             * The floor/ceiling this SCREEN imposes, on top of whatever the
             * schema says. Archive is past-only; tasking leaves it to the
             * schema's own `date_restriction`.
             *
             * Still scoped to the classic date_from/date_to keys, as before —
             * widening it to every date field would change archive behaviour
             * beyond the schema rules being added here.
             */
            const isClassicRangeKey = key === "date_from" || key === "date_to";
            const maxExtra =
               isClassicRangeKey && !disablePastDates ? dayjs().endOf("day") : null;

            const partnerKey = resolveRangePartnerKey(key, options);
            const isFromSide = isRangeFromSide(key, options);
            const showTime = !!config.show_time;

            // One window folding together min/max_date, date_restriction,
            // min/max_lead_hours and the gap to the paired field
            // (min/max_range_days and their _hours twins).
            const bounds = getDateFieldBounds({
               fieldKey: key,
               config,
               formData,
               options,
               maxExtra,
            });

            return (
               <DatePicker
                  format={getDateDisplayFormat(config)}
                  showTime={showTime ? { format: config.time_format ?? "HH:mm" } : false}
                  key={key}
                  className="w-full div-dropdown"
                  value={dateValue}
                  disabledDate={buildDisabledDate(bounds, showTime)}
                  disabledTime={buildDisabledTime(bounds, showTime)}
                  allowClear={config.allow_clear !== false}
                  onChange={(date) =>
                     setFormData((prev: any) =>
                     {
                        const next: any = { ...prev, [key]: date ? date.toISOString() : null };

                        // Moving the START can push the end date outside its
                        // window — drop it only then, so a still-valid pick
                        // survives the edit instead of being cleared blindly.
                        if (isFromSide && partnerKey &&
                           isPartnerValueStale({ partnerKey, formData: next, options, maxExtra }))
                        {
                           next[partnerKey] = null;
                        }

                        return next;
                     })
                  }
                  disabled={!isEditable(config)}
                  placeholder={t(config.placeholder)}
               />
            );
         }

         case "number":
            // NumberInput, not a bare InputNumber: AntD refuses an out-of-range
            // entry without a word and puts the old number back on blur, which
            // reads as the field undoing the user's edit. This one states the
            // bounds and explains any value it had to move.
            return (
               <NumberInput
                  key={key}
                  value={value ?? config.default}
                  min={config.min}
                  max={config.max}
                  step={config.step}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }))
                  }
                  disabled={!isEditable(config)}
                  placeholder={t(config.placeholder)}
               />
            );

         case "slider":
            // SlideInput, not a bare Slider: the number box in front of the track
            // is what shows the current value and lets it be typed instead of
            // dragged — the two stay in sync on the one formData entry.
            return (
               <SlideInput
                  key={key}
                  min={config.min ?? 0}
                  max={config.max ?? 100}
                  step={config.step ?? 1}
                  value={value ?? config.default ?? config.min ?? 0}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }))
                  }
                  disabled={!isEditable(config)}
               />
            );

         case "radio":
            return (
               <Radio.Group
                  key={key}
                  options={config.options?.map((o: any) => ({
                     label: t(o.label) || t(o.label) || t(o.value),
                     value: o.value,
                  }))}
                  value={value}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
               />
            );

         case "switch":
            return (
               <Switch
                  key={key}
                  checked={value ?? config.default ?? false}
                  onChange={(checked) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: checked,
                     }))
                  }
                  disabled={!isEditable(config)}
               />
            );

         case "range":
            // This is a standalone range field (not part of a pair)
            // For consistency, we still use RangeInput but with a single value
            // However, the RangeInput component expects a [min, max] tuple
            // So we'll render a placeholder if not paired
            return (
               <div key={key} className="text-red-400">
                  Range widget must be paired with another range field (min/max pair). Found unpaired range: {key}
               </div>
            );

         case "rangeInput":
         case "range_minmax":
            // RangeInput-like widgets: store value as [min, max] array in a single field
            const rmKey = key;
            const rmConfig = config;
            const rawValue = formData[rmKey];
            let rangeArray: [number, number];
            if (Array.isArray(rawValue) && rawValue.length === 2)
            {
               rangeArray = [rawValue[0], rawValue[1]];
            }
            else if (rawValue !== undefined && rawValue !== null)
            {
               // If a single number is provided, replicate it
               const num = typeof rawValue === 'number' ? rawValue : (rmConfig.defaultMin !== undefined ? rmConfig.defaultMin : rmConfig.default);
               rangeArray = [num, num];
            }
            else
            {
               // Priority: defaultMin/defaultMax > default array > default single > min/max fallback
               if (rmConfig.defaultMin !== undefined && rmConfig.defaultMax !== undefined)
               {
                  rangeArray = [rmConfig.defaultMin, rmConfig.defaultMax];
               }
               else if (rmConfig.defaultMin !== undefined)
               {
                  rangeArray = [rmConfig.defaultMin, rmConfig.defaultMin];
               }
               else if (rmConfig.defaultMax !== undefined)
               {
                  rangeArray = [rmConfig.defaultMax, rmConfig.defaultMax];
               }
               else if (rmConfig.default !== undefined)
               {
                  if (Array.isArray(rmConfig.default) && rmConfig.default.length === 2)
                  {
                     rangeArray = [rmConfig.default[0], rmConfig.default[1]];
                  }
                  else
                  {
                     const defaultNum = rmConfig.default;
                     rangeArray = [defaultNum, defaultNum];
                  }
               }
               else
               {
                  rangeArray = [rmConfig.min ?? 0, rmConfig.max ?? 100];
               }
            }

            return (
               <RangeInput
                  key={rmKey}
                  value={rangeArray}
                  min={rmConfig.min ?? 0}
                  max={rmConfig.max ?? 100}
                  // step={rmConfig.step ?? 1}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [rmKey]: val,
                     }))
                  }
                  disabled={!isEditable(rmConfig)}
               />
            );

         case "geo-json-editor":
            return null
         // Placeholder for future implementation
         // return (
         //       <div key={key} className="p-4 border border-yellow-500 rounded bg-[#1a2234]">
         //             <p className="text-yellow-500 mb-2">
         //                   geo-json-editor widget not yet implemented
         //             </p>
         //             <pre className="text-xs text-gray-400 overflow-auto max-h-60">
         //                   {JSON.stringify(value, null, 2)}
         //             </pre>
         //       </div>
         // );

         default:
            return (
               <div key={key} className="text-red-400">
                  Unsupported widget: {config.widget}
               </div>
            );
      }
   };

   const isFieldRequired = (_key: string, config: any) => !!config.required;


   const renderRangePair = (key1: string, key2: string, config1: any, config2: any) =>
   {
      const targetMin = key1;
      const targetMax = key2;
      const minVal = formData[targetMin] ?? config1.default ?? config1.min ?? 0;
      const maxVal = formData[targetMax] ?? config2.default ?? config2.max ?? 100;

      // Use the same min/max from either config (they should match)
      const rangeMin = config1.min ?? config2.min ?? 0;
      const rangeMax = config1.max ?? config2.max ?? 100;

      // Determine required status for the pair (use first field's config)
      const isRequired = isFieldRequired(key1, config1);

      // Create base label from config title, clean min/max prefix
      let baseLabel = config1.title || config1.label || key1;
      // Remove "Min " or "Max " prefix
      baseLabel = baseLabel.replace(/^(Min|Max)\s+/i, "");
      // Clean from key if still looks like key
      if (!baseLabel || baseLabel === key1)
      {
         baseLabel = key1.replace(/^(min|max)_/i, "");
         baseLabel = baseLabel.replace(/_/g, " ");
         baseLabel = baseLabel.charAt(0).toUpperCase() + baseLabel.slice(1);
      }

      return (
         <div>
            <div className="text-sm mb-1">
               <span className="text-gray-300">{t(baseLabel)}</span>
               {isRequired && <span className="text-red-500 ml-0.5">*</span>}
               {!isRequired && <span className="text-gray-500 ml-1">(optional)</span>}
            </div>
            <RangeInput
               label={undefined}
               value={[minVal, maxVal]}
               min={rangeMin}
               max={rangeMax}
               onChange={(val) =>
                  setFormData((prev: any) => ({
                     ...prev,
                     [targetMin]: val[0],
                     [targetMax]: val[1],
                  }))
               }

               disabled={!isEditable(config1)}
            />
         </div>
      );
   };

   return (
      // <div className="px-6 pb-6 space-y-4">
      <div className={display === 'grid' ? "px-6 pb-6 grid grid-cols-2 gap-4" : "px-6 pb-6 space-y-4"}>
         {(ui.order?.filter((itemF?: any) => itemF !== 'geometry') ?? []).map((key: string, index: number, order: string[]) =>
         {
            const config = ui?.options?.[key];

            if (!config)
            {
               return (
                  <div key={key} className="text-yellow-400">
                     Missing UI configuration for field: {key}
                  </div>
               );
            }

            // Check if field is conditionally hidden
            if (!shouldRenderField(key, config))
            {
               return null;
            }

            // Range min/max pairing — the partner can sit ANYWHERE in `order`,
            // not just adjacent. Pair by base name (min_off_nadir ↔ max_off_nadir).
            if (config.widget === "range")
            {
               const me = getRangeMeta(key);
               const partnerKey = findRangePartnerKey(key, order, ui?.options ?? {});

               if (partnerKey)
               {
                  const minKey = me.isMin ? key : partnerKey;
                  const maxKey = me.isMin ? partnerKey : key;
                  // Render the combined RangeInput once — at whichever half comes first.
                  const firstIdx = Math.min(
                     order.indexOf(minKey),
                     order.indexOf(maxKey),
                  );
                  if (index === firstIdx)
                  {
                     return (
                        <div key={`${minKey}-${maxKey}`}>
                           {renderRangePair(
                              minKey,
                              maxKey,
                              ui?.options?.[minKey],
                              ui?.options?.[maxKey],
                           )}
                        </div>
                     );
                  }
                  return null; // the other half is rendered together with its partner
               }
               // No partner → falls through to renderField (unpaired handling).
            }

            // Normal single field rendering
            return (
               <div key={key}>
                  <div className="text-sm mb-1">
                     {(() =>
                     {
                        const label = config.title || config.label || key;
                        const isRequired = isFieldRequired(key, config);

                        /**
                         * A dim note spelling out every rule on this field, so
                         * a greyed-out calendar is never a mystery.
                         *
                         * Built from the same inputs as the picker's own window
                         * and attached to the field the rule CONSTRAINS — a
                         * `min_range_days` written on the start field restricts
                         * the end one, and hinting it on the start left the end
                         * field silently unpickable.
                         */
                        const ruleHints =
                           config.widget === "date-picker"
                              ? getDateFieldRuleHints({
                                 fieldKey: key,
                                 config,
                                 formData,
                                 options: ui?.options || {},
                                 maxExtra:
                                    (key === "date_from" || key === "date_to") && !disablePastDates
                                       ? dayjs().endOf("day")
                                       : null,
                              })
                              : [];

                        const ruleHintText = ruleHints
                           .map((hint) =>
                              t(hint.i18nKey, {
                                 ...hint.values,
                                 ...(hint.partnerLabel
                                    ? { field: t(hint.partnerLabel) }
                                    : {}),
                              }),
                           )
                           .join(" · ");

                        return (
                           <>
                              <span className="text-gray-300">{t(label)}</span>
                              {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                              {!isRequired && <span className="text-gray-500 ml-1">({(t('optional'))})</span>}
                              {ruleHintText && (
                                 <span className="text-gray-500 ml-1 text-[11px]">
                                    ({ruleHintText})
                                 </span>
                              )}
                           </>
                        );
                     })()}
                  </div>
                  {renderField(key, config)}
                  {ui?.options?.[key]?.message_alert && (
                     <p className="text-sm text-yellow-400 mt-2 flex items-start gap-1">
                        <CommonIcons.InfoCircleIconAlert />
                        {t(ui?.options?.[key]?.message_alert)}
                     </p>
                  )}
               </div>
            );
         })}
      </div>
   );
};

export default UnifiedDynamicForm;
